import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import Formulario from "../../Componentes/Utils/Formulario";
import FormComp from "../../Componentes/FormComp";
import useApi from "../../hooks/useApi";
import useValidateColaborador from "../../hooks/useValidateColaborador";

const TIPOS_LICENCA_ENDPOINT = "/Painel/API/InventarioTI/GetTiposLicenca/";
const SAVE_COLAB_LICENCA_ENDPOINT = "/Painel/API/InventarioTI/SaveColabLicenca/";
const VERIFICAR_LICENCA_ATIVA_ENDPOINT =
  "/Painel/API/InventarioTI/VerificarColabLicencaAtiva/";

const SectionTitle = styled.p`
  margin: 4px 0 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #263b4b;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 38px;
  border-radius: 8px;
  border: 1px solid #94a3b8;
  padding: 0 12px;
  box-sizing: border-box;
  outline: none;
  font-size: 13px;

  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 1px #2563eb;
  }
`;

const ListShell = styled.div`
  margin-top: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  max-height: 260px;
  overflow-y: auto;
  background: #fff;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #c1c1c1;
    border-radius: 4px;
  }
`;

const ListHeader = styled.div`
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 12px;
  color: #475569;
  z-index: 1;
`;

const LicencaRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #eef2f7;
  cursor: pointer;
  font-size: 13px;
  color: #263b4b;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f1f5f9;
  }
`;

const LicencaLabel = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyRow = styled.div`
  padding: 18px 12px;
  text-align: center;
  font-size: 13px;
  color: #64748b;
`;

const Counter = styled.span`
  margin-left: auto;
  font-weight: 600;
  color: #263b4b;
`;

const formatLicencaLabel = (item) =>
  `${String(item?.fornecedor ?? "").trim()} - ${String(item?.tipo ?? "").trim()}`;

export default function ModalLancarLicencaColaborador({ onClose, onSaved }) {
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const [matricula, setMatricula] = useState("");
  const [email, setEmail] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [busca, setBusca] = useState("");
  const [tiposLicenca, setTiposLicenca] = useState([]);
  const [loadingTipos, setLoadingTipos] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selecionados, setSelecionados] = useState(() => new Set());
  const [verificandoLicencaAtiva, setVerificandoLicencaAtiva] = useState(false);
  const [licencaAtivaError, setLicencaAtivaError] = useState("");

  const {
    info: colabInfo,
    validating,
    error: validationError,
  } = useValidateColaborador(matricula, { bloquearDemitido: true });

  const colaboradorValidoProtheus =
    Boolean(colabInfo) && !validating && !validationError;

  const colaboradorValido =
    colaboradorValidoProtheus && !verificandoLicencaAtiva && !licencaAtivaError;

  const emailValido = useMemo(() => {
    const e = String(email || "").trim();
    if (!e) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  }, [email]);

  const canSubmit = useMemo(() => {
    return (
      matricula.trim().length === 6 &&
      colaboradorValido &&
      emailValido &&
      selecionados.size > 0 &&
      !saving &&
      !loadingTipos
    );
  }, [colaboradorValido, emailValido, loadingTipos, matricula, saving, selecionados.size]);

  useEffect(() => {
    const mat = String(matricula || "").trim();

    if (mat.length !== 6 || !colaboradorValidoProtheus) {
      setLicencaAtivaError("");
      setVerificandoLicencaAtiva(false);
      return;
    }

    let cancelled = false;

    const verificarLicencasAtivas = async () => {
      setVerificandoLicencaAtiva(true);
      setLicencaAtivaError("");

      try {
        const { data } = await apiRef.current.get(
          `${VERIFICAR_LICENCA_ATIVA_ENDPOINT}?matricula=${mat}`
        );

        if (cancelled) return;

        if (data?.possui_licenca_ativa) {
          setLicencaAtivaError(
            "Este colaborador já possui licenças ativas. Abra o cadastro na tabela para adicionar novas licenças."
          );
        }
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        setLicencaAtivaError(
          err?.response?.data?.message || "Erro ao verificar licenças ativas do colaborador."
        );
      } finally {
        if (!cancelled) {
          setVerificandoLicencaAtiva(false);
        }
      }
    };

    verificarLicencasAtivas();

    return () => {
      cancelled = true;
    };
  }, [matricula, colaboradorValidoProtheus]);

  useEffect(() => {
    const carregarTipos = async () => {
      setLoadingTipos(true);
      try {
        const { data } = await apiRef.current.get(TIPOS_LICENCA_ENDPOINT);
        const list = Array.isArray(data) ? data : data?.data ?? [];
        setTiposLicenca(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error(err);
        alert(err?.response?.data?.message || "Erro ao carregar tipos de licença.");
      } finally {
        setLoadingTipos(false);
      }
    };

    carregarTipos();
  }, []);

  const filtrados = useMemo(() => {
    const query = busca.trim().toUpperCase();
    if (!query) return tiposLicenca;
    return tiposLicenca.filter((item) =>
      formatLicencaLabel(item).toUpperCase().includes(query)
    );
  }, [busca, tiposLicenca]);

  const todosFiltradosSelecionados =
    filtrados.length > 0 && filtrados.every((item) => selecionados.has(String(item.id)));

  const toggleLicenca = (id) => {
    const key = String(id);
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleTodos = () => {
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (todosFiltradosSelecionados) {
        filtrados.forEach((item) => next.delete(String(item.id)));
      } else {
        filtrados.forEach((item) => next.add(String(item.id)));
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      setSaving(true);

      await apiRef.current.post(SAVE_COLAB_LICENCA_ENDPOINT, {
        matricula: matricula.trim(),
        email: email.trim(),
        observacoes: observacoes.trim() || null,
        ids_tipo_licenca: Array.from(selecionados).map((id) => Number(id)),
        lancamento_inicial: true,
      });

      onClose?.();
      onSaved?.();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Erro ao lançar licença(s).");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay state={true}>
      <Modal header close={onClose} size="md">
        <Formulario onSubmit={handleSubmit}>
          <FormComp
            title="MATRÍCULA DO COLABORADOR"
            type="numberOnly"
            placeholder="EX: 012345"
            valor={matricula}
            name="matricula"
            changeFunc={(e) => setMatricula(e.target.value.replace(/\D/g, ""))}
            required
            maxLength={6}
          />
          <FormComp
            title="NOME"
            type="text"
            readOnly
            valor={colabInfo?.nome || ""}
            placeholder={validating ? "Validando matrícula..." : ""}
          />
          <FormComp
            title="FUNÇÃO"
            type="text"
            readOnly
            valor={colabInfo?.funcao || ""}
          />
          <FormComp
            title="CENTRO DE CUSTO"
            type="text"
            readOnly
            valor={colabInfo?.centro_custo_desc || colabInfo?.centro_custo || ""}
          />
          {validationError && (
            <p style={{ color: "#B91C1C", fontSize: "12px", marginTop: "-6px" }}>
              {validationError}
            </p>
          )}
          {verificandoLicencaAtiva && colaboradorValidoProtheus && (
            <p style={{ color: "#475569", fontSize: "12px", marginTop: "-6px" }}>
              Verificando licenças ativas...
            </p>
          )}
          {licencaAtivaError && (
            <p style={{ color: "#B91C1C", fontSize: "12px", marginTop: "-6px" }}>
              {licencaAtivaError}
            </p>
          )}

          {colaboradorValido && (
            <>
              <FormComp
                title="E-MAIL DO COLABORADOR"
                type="email"
                placeholder="EX: colaborador@palmont.com.br"
                valor={email}
                name="email"
                changeFunc={(e) => setEmail(e.target.value)}
                required
                invalid={email.length > 0 && !emailValido}
              />
              <FormComp
                title="OBSERVAÇÕES"
                type="textarea"
                placeholder="INFORME OBSERVAÇÕES SOBRE AS LICENÇAS (OPCIONAL)"
                valor={observacoes}
                name="observacoes"
                changeFunc={(e) => setObservacoes(e.target.value)}
              />
            </>
          )}

          {colaboradorValido && (
            <div>
              <SectionTitle>TIPOS DE LICENÇA</SectionTitle>
              <SearchInput
                placeholder="Pesquisar por fornecedor ou tipo..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />

              <ListShell>
                <ListHeader>
                  <input
                    type="checkbox"
                    checked={todosFiltradosSelecionados}
                    onChange={toggleTodos}
                    disabled={filtrados.length === 0 || loadingTipos}
                    aria-label="Selecionar todos"
                  />
                  <span>Selecionar todos</span>
                  <Counter>
                    {selecionados.size} selecionada
                    {selecionados.size === 1 ? "" : "s"} • {tiposLicenca.length} tipo
                    {tiposLicenca.length === 1 ? "" : "s"}
                  </Counter>
                </ListHeader>

                {loadingTipos ? (
                  <EmptyRow>Carregando tipos de licença...</EmptyRow>
                ) : filtrados.length === 0 ? (
                  <EmptyRow>
                    {tiposLicenca.length === 0
                      ? "Nenhum tipo de licença cadastrado."
                      : "Nenhum tipo encontrado para a busca."}
                  </EmptyRow>
                ) : (
                  filtrados.map((item) => {
                    const label = formatLicencaLabel(item);
                    return (
                      <LicencaRow key={item.id} title={label}>
                        <input
                          type="checkbox"
                          checked={selecionados.has(String(item.id))}
                          onChange={() => toggleLicenca(item.id)}
                        />
                        <LicencaLabel>{label}</LicencaLabel>
                      </LicencaRow>
                    );
                  })
                )}
              </ListShell>
            </div>
          )}

          <FormComp
            type="submit"
            valor={
              saving
                ? "SALVANDO..."
                : validating || verificandoLicencaAtiva
                ? "VALIDANDO..."
                : "SALVAR"
            }
            disabled={saving || validating || verificandoLicencaAtiva || !canSubmit}
          />
        </Formulario>
      </Modal>
    </Overlay>
  );
}
